import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Icons } from '@ohif/ui-next';
import AppTooltip from '../AppTooltip/AppTooltip';
import ChangePasswordModal from '../ChangePasswordModal/ChangePasswordModal';
import ApiService from '../../services/ApiService';
import profileImg from '../../../assets/profile.png';
import logoWhite from '../../../assets/logo-white.png';
import loadingGif from '../../../assets/loading.gif';
import './AppHeader.css';

interface AppHeaderProps {
  title?: string;
  subtitle?: string;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  title = 'Grading Job Management',
  subtitle = 'Manage DICOM grading jobs and assign graders',
}) => {
  const apiService = ApiService.getInstance();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const notificationMenuRef = useRef<HTMLDivElement | null>(null);

  interface NotificationItem {
    id: number;
    message: string;
    time: string;
    read?: boolean;
    patientId?: string;
    submission?: string;
    alertId?: number;
  }

  const formatNotificationDate = (createdAt: string): string => {
    if (!createdAt) {
      return 'Just now';
    }

    try {
      const parsed = new Date(createdAt);

      if (!isNaN(parsed.getTime())) {
        // Date part: 12 March 2026
        const datePart = new Intl.DateTimeFormat('en-GB', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }).format(parsed);

        // Time part: 12:30 PM
        const timePart = new Intl.DateTimeFormat('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }).format(parsed);

        return `${datePart}, ${timePart}`;
      }

      // If parsing failed but we still have a string, show it as-is
      return typeof createdAt === 'string' ? createdAt : 'Just now';
    } catch {
      return typeof createdAt === 'string' ? createdAt : 'Just now';
    }
  };

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [notificationPage, setNotificationPage] = useState(1);
  const pageSize = 2;

  const markAlertsAsRead = async (alertsToMark: NotificationItem[]) => {
    if (!alertsToMark || alertsToMark.length === 0) {
      return;
    }

    const unreadAlertIds = alertsToMark
      .filter(alert => !alert.read && alert.alertId != null)
      .map(alert => Number(alert.alertId))
      .filter(id => Number.isFinite(id));

    if (unreadAlertIds.length === 0) {
      return;
    }

    try {
      const baseUrl = apiService.getUserManagementUrl();
      const response = await apiService.post<any>('/alert/mark', { alert_ids: unreadAlertIds }, baseUrl);

      if (!response.success) {
        console.error('AppHeader: Failed to mark alerts as read', response.error || response.data);
        return;
      }

      // Update local state so we don't send these IDs again
      setNotifications(prev =>
        prev.map(notification => {
          if (!notification.alertId) {
            return notification;
          }

          const idNum = Number(notification.alertId);
          if (Number.isFinite(idNum) && unreadAlertIds.includes(idNum)) {
            return {
              ...notification,
              read: true,
            };
          }

          return notification;
        })
      );
    } catch (error) {
      console.error('AppHeader: Error calling alert mark API', error);
    }
  };

  const getAlertsForPage = (page: number): NotificationItem[] => {
    if (!notifications.length) {
      return [];
    }

    const totalPagesForCalc = Math.max(1, Math.ceil(notifications.length / pageSize));
    const targetPage = Math.min(Math.max(page, 1), totalPagesForCalc);
    const startIndexForPage = (targetPage - 1) * pageSize;
    return notifications.slice(startIndexForPage, startIndexForPage + pageSize);
  };

  useEffect(() => {
    const loadNotificationsFromApi = async () => {
      try {
        const baseUrl = apiService.getUserManagementUrl();
        const response = await apiService.get<any>('/alert', undefined, baseUrl);

        const ok = response.is_success === true || response.success === true;
        const data = response.data;
        const raw: any[] = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);

        if (ok && raw.length > 0) {
          const mapped: NotificationItem[] = raw.map((item: any, index: number) => {
            const alert = item.alert || {};
            const meta = alert.meta || {};

            const listId =
              typeof item.id === 'number'
                ? item.id
                : Number.isFinite(Number(item.id))
                  ? Number(item.id)
                  : index + 1;

            const id = listId;
            const alertId = listId;
            const message = alert.message || alert.title || `Alert ${index + 1}`;
            const createdAt = alert.created_at || '';
            const time = formatNotificationDate(createdAt);
            const read = !!item.has_read;
            const patientId = meta.patient_id != null ? String(meta.patient_id) : undefined;
            const submission = meta.submission != null ? String(meta.submission) : undefined;

            return {
              id,
              message,
              time,
              read,
              patientId,
              submission,
              alertId,
            };
          });

          setNotifications(mapped);
          setNotificationPage(1);
          return;
        }

        setNotifications([]);
        setNotificationPage(1);
      } catch (error) {
        console.error('AppHeader: Failed to load alerts from API', error);
        setNotifications([]);
        setNotificationPage(1);
      }
    };

    loadNotificationsFromApi();
  }, [apiService, user?.username]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (profileMenuRef.current && !profileMenuRef.current.contains(target)) {
        setIsMenuOpen(false);
      }

      if (notificationMenuRef.current && !notificationMenuRef.current.contains(target)) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Define all menu items
  const allMenuItems = [
    {
      id: 'submissions',
      label: 'Submissions',
      path: '/submissions',
    },
    {
      id: 'studies',
      label: 'Studies',
      path: '/studiesList',
      state: { from: 'menu' },
    },
    {
      id: 'admin',
      label: 'Administration',
      path: '/admin/users',
      roleRequired: 'admin', // Only show for admin users
    },
    {
      id: 'audit',
      label: 'Audit Trial',
      path: '/audit',
    },
  ];

  // Filter menu items based on user role
  const menuItems = React.useMemo(() => {
    return allMenuItems.filter(item => {
      // If item requires a specific role, check if user has that role (case-insensitive)
      if (item.roleRequired) {
        return user?.role?.toLowerCase() === item.roleRequired.toLowerCase();
      }
      // Otherwise, show the item to everyone
      return true;
    });
  }, [user?.role]);

  const handleMenuClick = (path: string, state?: any) => {
    if (state) {
      navigate(path, { state });
    } else {
      navigate(path);
    }
  };

  const isActiveMenu = (path: string) => {
    // Submissions, Studies, Audit should match exactly,
    // but any /admin/* path should keep Administration highlighted.
    if (path.startsWith('/admin')) {
      return location.pathname.startsWith('/admin');
    }

    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
    } catch (error) {
      console.error('AppHeader: Failed to logout', error);
      setIsLoggingOut(false);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
    setIsMenuOpen(false);
  };

  const handleCancelLogout = () => {
    if (isLoggingOut) {
      return;
    }
    setShowLogoutConfirm(false);
  };

  const handleOpenChangePassword = () => {
    setIsMenuOpen(false);
    setShowChangePassword(true);
  };

  const handleCloseChangePassword = () => {
    setShowChangePassword(false);
  };

  const handleToggleNotifications = () => {
    setIsNotificationOpen(prev => {
      const next = !prev;
      if (next) {
        // Reset to first page when opening the panel
        setNotificationPage(1);

        // Mark only the alerts visible on the first page as read
        const firstPageAlerts = getAlertsForPage(1);
        void markAlertsAsRead(firstPageAlerts);
      }
      return next;
    });
    // Close profile menu when opening notifications
    setIsMenuOpen(false);
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const totalPages = Math.max(1, Math.ceil(notifications.length / pageSize));
  const clampedPage = Math.min(notificationPage, totalPages);
  const startIndex = (clampedPage - 1) * pageSize;
  const pagedNotifications = notifications.slice(startIndex, startIndex + pageSize);

  const handlePrevPage = () => {
    setNotificationPage(prev => {
      const newPage = Math.max(1, prev - 1);
      const alertsOnPage = getAlertsForPage(newPage);
      void markAlertsAsRead(alertsOnPage);
      return newPage;
    });
  };

  const handleNextPage = () => {
    setNotificationPage(prev => {
      const newPage = Math.min(totalPages, prev + 1);
      const alertsOnPage = getAlertsForPage(newPage);
      void markAlertsAsRead(alertsOnPage);
      return newPage;
    });
  };

  return (
    <div className="app-header">
      <div className="header-content">
        <div className="header-logo">
          <img
            src={logoWhite}
            alt="Chaithanya Eye Hospital & Research Institute"
            className="logo-image"
          />
        </div>

        <div className="header-menu">
          <nav className="main-navigation">
            {menuItems.map(item => (
              <button
                key={item.id}
                className={`menu-item ${isActiveMenu(item.path) ? 'active' : ''}`}
                onClick={() => handleMenuClick(item.path, (item as any).state)}
                aria-label={item.label}
              >
                <span className="menu-label">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="header-actions">
          <div
            className="notification-menu"
            ref={notificationMenuRef}
          >
            <AppTooltip content="Notifications" side="bottom" sideOffset={6}>
              <button
                className="notification-bell"
                onClick={handleToggleNotifications}
                aria-haspopup="true"
                aria-expanded={isNotificationOpen}
                aria-label="Notifications"
              >
                <svg
                  className="notification-bell-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M12 22c1.104 0 2-.9 2-2h-4c0 1.1.896 2 2 2Zm7-6v-3.5c0-3.07-1.64-5.64-4.5-6.32V5a2.5 2.5 0 1 0-5 0v1.18C6.64 6.86 5 9.43 5 12.5V16l-1.71 1.71A1 1 0 0 0 3.59 19h16.82a1 1 0 0 0 .7-1.71L19 16Z"
                    fill="currentColor"
                  />
                </svg>
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount}</span>
                )}
              </button>
            </AppTooltip>

            {isNotificationOpen && (
              <div
                className="notification-panel"
                role="list"
              >
                <div className="notification-panel-header">
                  <span className="notification-panel-title">Notifications</span>
                  <span className="notification-panel-subtitle">
                    {notifications.length > 0
                      ? `${notifications.length} message${notifications.length > 1 ? 's' : ''}`
                      : 'No notifications'}
                  </span>
                </div>
                <div className="notification-list">
                  {notifications.length === 0 ? (
                    <div className="notification-empty" role="status">
                      No data available
                    </div>
                  ) : (
                    pagedNotifications.map(notification => (
                      <div
                        key={notification.id}
                        className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                        role="listitem"
                      >
                        <div className="notification-item-icon">
                          <Icons.NotificationInfo />
                        </div>
                        <div className="notification-item-content">
                          <div className="notification-message">{notification.message}</div>
                          {(notification.patientId != null /* || notification.submission != null */) && (
                            <div className="notification-meta">
                              {notification.patientId != null && (
                                <span className="notification-pid">pid: {notification.patientId}</span>
                              )}
                              {/* submission hidden for now
                              {notification.submission != null && (
                                <span className="notification-submission">
                                  submission: {notification.submission}
                                </span>
                              )}
                              */}
                            </div>
                          )}
                          <div className="notification-time">{notification.time}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {notifications.length > 0 && (
                <div className="notification-panel-footer">
                  <button
                    type="button"
                    className="notification-page-button"
                    onClick={handlePrevPage}
                    disabled={clampedPage === 1}
                  >
                    <Icons.ArrowLeft className="notification-page-icon" />
                  </button>
                  <span className="notification-page-indicator">
                    Page {clampedPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="notification-page-button"
                    onClick={handleNextPage}
                    disabled={clampedPage === totalPages}
                  >
                    <Icons.ArrowRight className="notification-page-icon" />
                  </button>
                </div>
                )}
              </div>
            )}
          </div>
          <div className="user-info">
            <div className="user-name">{user?.username || 'User'}</div>
            <div className="user-role italic">
              {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Guest'}
            </div>
          </div>

          <div
            className="profile-menu"
            ref={profileMenuRef}
          >
            <AppTooltip content="Profile menu" side="bottom" sideOffset={6}>
              <button
                className="profile-avatar"
                onClick={() => setIsMenuOpen(prev => !prev)}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
              >
                <img
                  src={profileImg}
                  alt="Profile"
                />
              </button>
            </AppTooltip>

            {isMenuOpen && (
              <div
                className="profile-dropdown"
                role="menu"
              >
                <button
                  className="dropdown-item"
                  onClick={handleOpenChangePassword}
                  role="menuitem"
                >
                  <Icons.Lock className="dropdown-icon" />
                  <span>Change password</span>
                </button>
                <button
                  className="dropdown-item"
                  onClick={handleLogoutClick}
                  role="menuitem"
                >
                  <Icons.PowerOff className="dropdown-icon" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showLogoutConfirm && (
        <div
          className="modal-overlay top-center"
          onClick={handleCancelLogout}
        >
          <div
            className="modal-content popup-w"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '420px' }}
          >
            <div className="modal-header">
              <h3>Confirm Logout</h3>
              <button
                className="modal-close"
                aria-label="Close logout confirmation"
                onClick={handleCancelLogout}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '12px' }}>
                Are you sure you want to logout?
              </p>
              <p style={{ fontSize: '13px', color: '#cbd5e0' }}>
                You will need to sign in again to continue working.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="modal-cancel"
                onClick={handleCancelLogout}
                disabled={isLoggingOut}
              >
                Cancel
              </button>
              <button
                className="modal-confirm"
                onClick={handleLogout}
                disabled={isLoggingOut}
                aria-busy={isLoggingOut}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoggingOut && (
        <div className="logout-loading-overlay" role="status" aria-live="polite">
          <img src={loadingGif} alt="Logging out..." className="logout-loading-spinner" />
        </div>
      )}

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={handleCloseChangePassword}
      />
    </div>
  );
};

export default AppHeader;
